import React, { useEffect, useState } from "react";
import Head from "next/head";
import { useTranslation } from "next-i18next";
import businessDivisions from "../../../data/businessDivision";
import { Footer } from "../../../components/LandingPage/footer";
import { Contact } from "../../../components/LandingPage/Contact";
import { TopbarLanding } from "../../../components/LandingPage/topbar";
import { FaceLiftGlobalStyle } from "../../../styles/theme";
import DefaultDarkModeHOC from "../../../components/theme/DefaultDarkModeHOC";
import { SeamV2 } from "../../../components/LandingPage/v2/primitives";
import DIVISION_THEMES from "../../../data/businessDivisionThemes";
import useSelectedLanguageCode from "../../../hooks/useSelectedLanguageCode";
import {
	getI18nPaths,
	getI18nProps,
} from "../../../lib/next-i18next/getStatic";
import Hero from "./components/Hero";
import Journey from "./components/Journey";
import Metrics from "./components/Metrics";
import Products from "./components/Products";

/**
 * The division landings: same #000104 particle-cinema language as the
 * redesigned home sections. Theming (accent, cinema mode, sculpture) comes
 * from the shared DIVISION_THEMES map (src/data/businessDivisionThemes.js),
 * which also drives the static paths built below.
 */
const BusinessDivision = ({ businessDivision }) => {
	const { t } = useTranslation();
	const selectedLanguageCode = useSelectedLanguageCode();
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
	}, []);

	if (!businessDivision) return null;
	const theme = DIVISION_THEMES[businessDivision.id];

	const origin =
		process.env.NEXT_PUBLIC_WEBSITE_DOMAIN || "https://www.verzel.com.br";
	const pageTitle = `Verzel - ${t(`businessDivision.${businessDivision.id}`)}`;
	const pageDescription = t(`businessDivision.${businessDivision.id}Text`);
	const pageUrl = `${origin}/${selectedLanguageCode}/business-division/${businessDivision.id}/`;
	const pageImage = `${origin}/landings/assets/images/LogoVerzelHorizontal.svg`;

	return (
		<DefaultDarkModeHOC>
			<Head>
				<title>{pageTitle}</title>
				<meta name="title" content={pageTitle} />
				<meta name="description" content={pageDescription} />

				<meta property="og:type" content="website" />
				<meta property="og:url" content={pageUrl} />
				<meta property="og:title" content={pageTitle} />
				<meta property="og:description" content={pageDescription} />
				<meta property="og:image" content={pageImage} />

				<meta property="twitter:card" content="summary_large_image" />
				<meta property="twitter:url" content={pageUrl} />
				<meta property="twitter:title" content={pageTitle} />
				<meta property="twitter:description" content={pageDescription} />
				<meta property="twitter:image" content={pageImage} />
			</Head>

			<FaceLiftGlobalStyle />
			<TopbarLanding />
			{/* keyed by division: client-side hops between slugs reuse this
			    page component, and the hero's cinema engine (mounted once) would
			    keep playing the previous division's scene; a remount also replays
			    the arrival choreography, which is the point of the transition */}
			<Hero
				key={`hero-${businessDivision.id}`}
				division={businessDivision}
				theme={theme}
				selectedLanguage={selectedLanguageCode}
			/>
			<SeamV2 />
			<Journey
				key={`journey-${businessDivision.id}`}
				divisionId={businessDivision.id}
				theme={theme}
			/>
			<SeamV2 />
			<Metrics
				key={`metrics-${businessDivision.id}`}
				divisionId={businessDivision.id}
				infos={businessDivision.infos}
				selectedLanguage={selectedLanguageCode}
				theme={theme}
			/>
			<SeamV2 />
			<Products
				key={`products-${businessDivision.id}`}
				divisionId={businessDivision.id}
				products={businessDivision.products}
				selectedLanguage={selectedLanguageCode}
				theme={theme}
			/>
			{mounted && <Contact />}
			<Footer />
		</DefaultDarkModeHOC>
	);
};

export async function getStaticProps(ctx) {
	const i18nProps = await getI18nProps(ctx);

	return {
		props: {
			...i18nProps,
			division: ctx?.params.slug,
			businessDivision:
				businessDivisions.find((bd) => bd.id === ctx?.params.slug) || null,
		},
	};
}

export async function getStaticPaths() {
	const divisionPaths = Object.keys(DIVISION_THEMES).map((slug) => ({ slug }));

	return {
		paths: getI18nPaths(divisionPaths),
		fallback: false,
	};
}

export default BusinessDivision;
